
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { BellIcon, ChevronDownIcon, Square2StackIcon } from '@heroicons/vue/20/solid'
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'

import NeueTablist from '@/components/neue/NeueTablist.vue'
import TablistHeader from '@/components/neue/TablistHeader.vue'
import TablistRow from '@/components/neue/TablistRow.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import NeuePagination from '@/components/neue/NeuePagination.vue'
import { serverDayToDateObject } from '@/helpers/dates'
import { dropStatus as dropStatusFor } from '@/helpers/dropStatus'
import { duplicatorRecurrenceDescription } from '@/helpers/duplicator'
import api from '@/store/api'
import { mapRelationship, mapRelationships } from '@/store/mappers'

export default {
  components: {
    BellIcon,
    ChevronDownIcon,
    Square2StackIcon,
    Popover,
    PopoverButton,
    PopoverPanel,
    NeueTablist,
    TablistHeader,
    TablistRow,
    TablistGrid,
    NeuePagination
  },
  props: {
    // Row click destination prefix; the drop id is appended.
    linkBase: { type: String, default: '/store/drops' }
  },
  setup() {
    const store = useStore()
    const loading = ref(true)
    const dropFilter = ref('upcoming')

    const dropColumns = [
      { width: '60px' },
      { width: 'minmax(220px, 2fr)' },
      { width: 'minmax(160px, 1fr)' },
      { width: 'minmax(120px, 1fr)' },
      { width: 'minmax(130px, 1fr)' },
      { width: '110px' },
      { width: '50px' },
      // Wide enough for the longest badge, "Reminder failed", on one line.
      { width: '130px' }
    ]

    const data = reactive({
      list: computed(() =>
        Object.values(store.state.objects.bottle_cycle || {})
          .sort((a, b) => a.id - b.id)
          .sort((a, b) => {
            const aDay = mapRelationships(a.relationships.fulfillment_availabilities.data).sort(
              (x, y) => new Date(x.attributes.date) - new Date(y.attributes.date)
            )[0]
            const bDay = mapRelationships(b.relationships.fulfillment_availabilities.data).sort(
              (x, y) => new Date(x.attributes.date) - new Date(y.attributes.date)
            )[0]
            return new Date(aDay?.attributes.date) - new Date(bDay?.attributes.date)
          })
      ),
      page: 0
    })

    const groupsForDrop = drop =>
      mapRelationships(drop.relationships.bottle_cycle_audience_pairs.data).map(x =>
        mapRelationship({ type: 'audience', id: x.attributes.audience_id })
      )

    const setDropFilter = value => {
      dropFilter.value = value
      data.page = 0
    }

    const dropFilterLabel = computed(() => {
      if (dropFilter.value === 'upcoming') return 'Upcoming'
      if (dropFilter.value === 'past') return 'Past'
      return 'No production days'
    })

    // Shown when the merchant has drops but none in the view they picked, so
    // the message names the view rather than claiming they have no drops.
    const emptyViewLabel = computed(() => {
      if (dropFilter.value === 'upcoming') return 'upcoming drops'
      if (dropFilter.value === 'past') return 'past drops'
      return 'drops without production days'
    })

    const emptyViewDescription = computed(
      () => `Change the ${dropFilterLabel.value} filter above to see your other drops.`
    )

    const dropStatus = drop => dropStatusFor(drop, remindersForDrop(drop))

    const storeForDrop = drop => drop.relationships.store.data && mapRelationship(drop.relationships.store.data)

    const duplicatorForDrop = drop =>
      drop.relationships.duplicator.data && mapRelationship(drop.relationships.duplicator.data)

    const recurrenceForDrop = drop => duplicatorRecurrenceDescription(duplicatorForDrop(drop)?.attributes)

    const autochargeTimeForDrops = drop => {
      if (!drop.relationships.bottle_autocharge_automation.data) return
      const autocharge = mapRelationship(drop.relationships.bottle_autocharge_automation.data)
      const automation = mapRelationship(autocharge.relationships.automation_scheduler.data)
      if (!automation) return
      return new Intl.DateTimeFormat([], {
        hour: 'numeric',
        minute: 'numeric'
      }).format(new Date(automation.attributes.datetime))
    }

    const remindersForDrop = drop => {
      if (!drop.relationships.bottle_reminder_automations.data) return []
      return mapRelationships(drop.relationships.bottle_reminder_automations.data)
    }

    const fulfillmentDaysForDrop = drop =>
      mapRelationships(drop.relationships.fulfillment_availabilities.data).map(x =>
        Intl.DateTimeFormat([], {
          day: 'numeric',
          month: 'short'
        }).format(serverDayToDateObject(x.attributes.date))
      )

    const getList = () => {
      const includes = [
        'audiences',
        'bottle_reminder_automations',
        'bottle_autofill_automation',
        'bottle_autocharge_automation',
        'fulfillment_availabilities',
        'duplicator',
        'store',
        'bottle_cycle_audience_pairs',
        'bottle_reminder_automations.automation_scheduler',
        'bottle_reminder_automations.message_template',
        'bottle_reminder_automations.message_template.attachments',
        'bottle_autofill_automation.automation_fields',
        'bottle_autocharge_automation.automation_scheduler'
      ]
      api.call('GET', `bottle_cycles?included=${includes.join(',')}`).then(() => {
        loading.value = false
      })
    }

    onMounted(() => {
      getList()
    })

    const upcomingDrops = computed(() =>
      data.list
        .filter(x =>
          mapRelationships(x.relationships.fulfillment_availabilities.data).some(
            y => serverDayToDateObject(y.attributes.date) > new Date()
          )
        )
        .sort((a, b) => {
          const aDay = mapRelationships(a.relationships.fulfillment_availabilities.data)
            .filter(x => serverDayToDateObject(x.attributes.date) > new Date())
            .sort((x, y) => new Date(x.attributes.date) - new Date(y.attributes.date))[0]
          const bDay = mapRelationships(b.relationships.fulfillment_availabilities.data)
            .filter(x => serverDayToDateObject(x.attributes.date) > new Date())
            .sort((x, y) => new Date(x.attributes.date) - new Date(y.attributes.date))[0]
          return new Date(aDay?.attributes.date) - new Date(bDay?.attributes.date)
        })
    )

    const pastDrops = computed(() =>
      data.list
        .filter(x =>
          mapRelationships(x.relationships.fulfillment_availabilities.data).some(
            y => serverDayToDateObject(y.attributes.date) < new Date()
          )
        )
        .sort((a, b) => {
          const aDay = mapRelationships(a.relationships.fulfillment_availabilities.data)
            .filter(x => serverDayToDateObject(x.attributes.date) < new Date())
            .sort((x, y) => new Date(y.attributes.date) - new Date(x.attributes.date))[0]
          const bDay = mapRelationships(b.relationships.fulfillment_availabilities.data)
            .filter(x => serverDayToDateObject(x.attributes.date) < new Date())
            .sort((x, y) => new Date(y.attributes.date) - new Date(x.attributes.date))[0]
          return new Date(bDay?.attributes.date) - new Date(aDay?.attributes.date)
        })
    )

    const noDaysDrops = computed(() =>
      data.list.filter(x => x.relationships.fulfillment_availabilities.data.length === 0).sort((a, b) => b.id - a.id)
    )

    const activeList = computed(() => {
      if (dropFilter.value === 'upcoming') return upcomingDrops.value
      if (dropFilter.value === 'past') return pastDrops.value
      return noDaysDrops.value
    })

    return {
      data,
      groupsForDrop,
      storeForDrop,
      autochargeTimeForDrops,
      remindersForDrop,
      fulfillmentDaysForDrop,
      duplicatorForDrop,
      recurrenceForDrop,
      setDropFilter,
      dropFilterLabel,
      emptyViewLabel,
      emptyViewDescription,
      activeList,
      dropStatus,
      loading,
      dropColumns
    }
  }
}
